import { useState, useEffect, Fragment } from "react";
import { Routes, Route, Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { getImageUrl } from "../../utils/imageUtils";
import { Button } from "@/components/ui/button";
// Reseller Pages
import ResellerOverview from "./ResellerOverview";
import ResellerSettings from "./ResellerSettings";
import ResellerPricing from "./ResellerPricing";
import ResellerProducts from "./ResellerProducts";
import ResellerCollections from "./ResellerCollections"; // Import the new page
import ResellerEarnings from "./ResellerEarnings";
import ResellerOrders from "./ResellerOrders";
import ResellerFuneralStore from "./ResellerFuneralStore";
import FuneralStoreProductDetails from "./FuneralStoreProductDetails";
import ResellerMyOrders from "./ResellerMyOrders";
import SavedPrintOrders from "./SavedPrintOrders";
import PrintRecordDetail from "./PrintRecordDetail";
import PrintOrderFlow from "./PrintOrderFlow";
import PrintOrders from "./PrintOrders";
import CommissionLedger from "./CommissionLedger";
import Checkout from "../Checkout";
import { getStorePreviewUrl } from "../../utils/storeUtils";
import {
  LayoutDashboard,
  Store,
  Settings,
  Palette,
  Share2,
  DollarSign,
  FolderPlus,
  LogOut,
  Menu,
  X,
  Eye,
  TrendingUp,
  Mail,
  Phone,
  HelpCircle,
  Package,
  Layers,
  ShoppingCart,
  FileText,
  ClipboardList,
  Receipt,
  ChevronDown,
  Sparkles
} from "lucide-react";
import { useCart } from "../../context/CartContext";
import { CartSheet } from "../../components/store";

import { usePlatformContext } from "../../context/PlatformContext";
import { ModuleProvider } from "../../context/ModuleContext";
import ModuleRoute from "../../components/reseller/ModuleRoute";
import TermsGate from "../../components/reseller/TermsGate";
import { resolveModuleStates } from "../../utils/storeModules";

export default function ResellerDashboard() {
  const location = useLocation();
  const navigate = useNavigate();
  const { cartCount, setCartOpen, clearCart } = useCart();
  const { platformInfo } = usePlatformContext();
  const [user, setUser] = useState(null);
  const [store, setStore] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Which platform groups are expanded. Seeded below from the active route so a
  // deep link never lands on a fully collapsed sidebar with nothing highlighted.
  const [openGroups, setOpenGroups] = useState({});
  const [termsStatus, setTermsStatus] = useState(null);

  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  const isActive = (path, exact = false) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  const moduleStates = resolveModuleStates(store);

  // Navigation is grouped by platform. Each module group is shown when the
  // funeral home has it now or had it at some point — the latter keeps their
  // history reachable, read-only, after an admin switches it off. A module
  // that was never enabled contributes nothing to the sidebar.
  //
  // The two untitled groups are deliberately ungated: Funeral Supplies and My
  // Purchases are the funeral home buying *from* the platform, and Earnings is
  // money already accrued. None of that is scoped by a platform subscription,
  // so do not fold them into a module group "for consistency".
  //
  // Commission Ledger is hidden for now (route + page kept; re-add when ready).
  const navGroups = [
    {
      key: "core",
      items: [
        { path: "/reseller", icon: LayoutDashboard, label: "Overview", exact: true },
        { path: "/reseller/settings", icon: Settings, label: "Business Info" },
      ],
    },
    {
      key: "edisplayroom",
      label: "E-DisplayRoom",
      icon: Store,
      items: [
        { path: "/reseller/orders", icon: ShoppingCart, label: "ED Orders" },
        { path: "/reseller/products", icon: Package, label: "My Products" },
        { path: "/reseller/collections", icon: Layers, label: "My Collections" },
        { path: "/reseller/pricing", icon: DollarSign, label: "Pricing" },
      ],
    },
    {
      key: "infinity_touch",
      label: "InfinityTouch",
      icon: Sparkles,
      items: [
        { path: "/reseller/print-records", icon: FileText, label: "Saved Print" },
        { path: "/reseller/print-orders", icon: ClipboardList, label: "Print Orders" },
        // { path: "/reseller/commission-ledger", icon: Receipt, label: "Commission Ledger" },
      ],
    },
    {
      key: "core_tail",
      items: [
        { path: "/reseller/earnings", icon: TrendingUp, label: "Earnings" },
        { path: "/reseller/funeral-store", icon: Store, label: "Funeral Supplies" },
        { path: "/reseller/my-purchases", icon: ShoppingCart, label: "My Purchases" },
      ],
    },
  ];

  const visibleGroups = navGroups.filter(
    (g) => !moduleStates[g.key] || moduleStates[g.key].visible
  );

  // Longest path first: isActive matches on prefix, so a shorter route would
  // otherwise shadow a longer sibling and the header would name the wrong page.
  const allNavItems = visibleGroups
    .flatMap((g) => g.items)
    .slice()
    .sort((a, b) => b.path.length - a.path.length);
  const activeItem = allNavItems.find((item) => isActive(item.path, item.exact));

  const isCheckoutPage = location.pathname.includes('/funeral-store/checkout');


  const fetchData = async () => {
    try {
      const [userRes, storeRes, termsRes] = await Promise.all([
        axios.get(`${API}/auth/me`),
        axios.get(`${API}/stores/my`).catch(() => ({ data: null })),
        // The server decides whether this funeral home owes an acceptance; the
        // panel only renders the answer. A failure here must not lock anyone
        // out of their own dashboard, so it degrades to "nothing owed".
        axios.get(`${API}/stores/my/terms-status`).catch(() => ({ data: null }))
      ]);
      setUser(userRes.data);
      setStore(storeRes.data);
      setTermsStatus(termsRes.data);
    } catch (error) {
      navigate('/login');
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
      localStorage.removeItem('session_token');
      navigate('/login');
    } catch (error) {
      localStorage.removeItem('session_token');
      navigate('/login');
    }
  };

  // Expand whichever platform group owns the current route. Without this a deep
  // link into e.g. /reseller/pricing renders every group collapsed, with no
  // visible indication of where you are.
  useEffect(() => {
    const owner = visibleGroups.find(
      (g) => g.label && g.items.some((item) => isActive(item.path, item.exact))
    );
    if (owner) setOpenGroups((prev) => (prev[owner.key] ? prev : { ...prev, [owner.key]: true }));
    // Intentionally keyed on the route and the store only — visibleGroups is
    // rebuilt every render, and depending on it would loop.
  }, [location.pathname, store]);

  if (!store) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="card-elevated p-8 text-center max-w-md">
          <div className="w-16 h-16 bg-amber-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <Store className="w-8 h-8 text-amber-600" />
          </div>
          <h2 className="text-xl font-bold text-slate-900 mb-2">
            No Store Found
          </h2>
          <p className="text-slate-600 mb-6">
            You do not have a store yet. Submit a request to get started!
          </p>
          <Button
            onClick={() => navigate('/')}
            className="bg-indigo-600 hover:bg-indigo-700 rounded-full"
          >
            Request a Store
          </Button>
        </div>
      </div>
    );
  }

  // Blocking terms acceptance. Placed here, in the shared shell and above every
  // route, so no /reseller/* page can be reached — or even mount and start
  // fetching — until the funeral home has accepted.
  if (termsStatus?.must_accept) {
    return (
      <TermsGate
        status={termsStatus}
        onAccepted={fetchData}
        onLogout={handleLogout}
      />
    );
  }

  return (
    <ModuleProvider store={store} refreshStore={fetchData}>
    <div className="h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50/30 flex overflow-hidden" data-testid="reseller-dashboard">
      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-white/80 backdrop-blur-xl border-r border-indigo-100/50 transform ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 transition-transform duration-200`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-indigo-100/50 bg-gradient-to-r from-indigo-50/50 to-transparent">
            <div className="flex items-center gap-3">
              {store.logo_url ? (
                <img src={getImageUrl(store.logo_url)} alt={store.business_name} className="h-12 w-auto object-contain" />
              ) : platformInfo.platform_logo_url ? (
                <img src={getImageUrl(platformInfo.platform_logo_url)} alt={platformInfo.platform_name} className="h-12 w-auto object-contain" />
              ) : (
                <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-indigo-700 rounded-xl flex items-center justify-center shadow-lg shadow-indigo-200">
                  <Store className="w-5 h-5 text-white" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <span className="text-lg font-bold text-slate-900 truncate block">
                  {store.business_name}
                </span>
                <span className="text-[10px] text-slate-500 truncate block w-full">
                  {getStorePreviewUrl(store.subdomain).replace(/^https?:\/\//, '')}
                </span>
              </div>
            </div>
          </div>

          <div className="p-4">
            <a href={getStorePreviewUrl(store.subdomain)} target="_blank" rel="noopener noreferrer">
              <Button
                className="w-full bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white rounded-xl shadow-lg shadow-emerald-200"
                data-testid="preview-store-btn"
              >
                <Eye className="w-4 h-4 mr-2" />
                Preview Store
              </Button>
            </a>
          </div>

          {/* Navigation - Scrollable */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto custom-scrollbar">
            {visibleGroups.map((group) => {
              const navLink = (item, nested) => {
                const active = isActive(item.path, item.exact);
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`flex items-center gap-3 px-4 rounded-xl transition-all ${nested ? 'py-2.5 text-sm' : 'py-3'} ${active
                      ? 'bg-gradient-to-r from-indigo-100 to-indigo-50 text-indigo-700 font-medium shadow-sm'
                      : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                      }`}
                    onClick={() => setSidebarOpen(false)}
                    data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <item.icon className={`w-5 h-5 ${active ? 'text-indigo-600' : ''}`} />
                    {item.label}
                  </Link>
                );
              };

              // Ungated groups render flat, as before.
              if (!group.label) {
                return <Fragment key={group.key}>{group.items.map((item) => navLink(item, false))}</Fragment>;
              }

              const state = moduleStates[group.key];
              const expanded = !!openGroups[group.key];
              const GroupIcon = group.icon;
              return (
                <div key={group.key} className="pt-1">
                  <button
                    onClick={() => setOpenGroups((prev) => ({ ...prev, [group.key]: !prev[group.key] }))}
                    className="w-full flex items-center justify-between px-4 py-3 rounded-xl text-xs font-semibold uppercase tracking-wider text-slate-400 hover:text-slate-600 hover:bg-slate-50 transition-all"
                    data-testid={`nav-group-${group.key}`}
                  >
                    <span className="flex items-center gap-2 min-w-0">
                      <GroupIcon className="w-4 h-4 shrink-0" />
                      <span className="truncate">{group.label}</span>
                      {/* Make the read-only state legible before you click into it. */}
                      {state?.readOnly && (
                        <span className="shrink-0 normal-case tracking-normal text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700">
                          Read-only
                        </span>
                      )}
                    </span>
                    <ChevronDown className={`w-4 h-4 shrink-0 transition-transform ${expanded ? '' : '-rotate-90'}`} />
                  </button>
                  {expanded && (
                    <div className="mt-1 ml-2 pl-2 border-l border-indigo-100 space-y-1">
                      {group.items.map((item) => navLink(item, true))}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>

          {/* Support Info */}
          {(platformInfo.support_email || platformInfo.support_phone) && (
            <div className="p-4 border-t border-indigo-100/50">
              <div className="bg-gradient-to-r from-slate-50 to-indigo-50/50 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-3">
                  <HelpCircle className="w-4 h-4 text-indigo-600" />
                  <span className="text-sm font-medium text-slate-900">Need Help?</span>
                </div>
                <div className="space-y-2">
                  {platformInfo.support_email && (
                    <a
                      href={`mailto:${platformInfo.support_email}`}
                      className="flex items-center gap-2 text-xs text-slate-600 hover:text-indigo-600 transition-colors"
                    >
                      <Mail className="w-3.5 h-3.5" />
                      {platformInfo.support_email}
                    </a>
                  )}
                  {platformInfo.support_phone && (
                    <a
                      href={`tel:${platformInfo.support_phone}`}
                      className="flex items-center gap-2 text-xs text-slate-600 hover:text-indigo-600 transition-colors"
                    >
                      <Phone className="w-3.5 h-3.5" />
                      {platformInfo.support_phone}
                    </a>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </aside>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/20 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className="flex-1 min-w-0 flex flex-col h-screen overflow-hidden">
        {/* Top Bar */}
        <header className="bg-white/80 backdrop-blur-xl border-b border-indigo-100/50 sticky top-0 z-30 h-16 flex items-center justify-between px-4 lg:px-8">
          <div className="flex items-center">
            <button
              className="lg:hidden p-2 mr-4"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              data-testid="toggle-sidebar-btn"
            >
              {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
            <h1 className="text-xl font-semibold text-slate-900">
              {activeItem?.label || 'Dashboard'}
            </h1>
          </div>

          {/* User Info & Logout - Top Right */}
          <div className="flex items-center gap-4">
            {!isCheckoutPage && (
              <Button
                variant="ghost"
                size="icon"
                className="relative rounded-full hover:bg-slate-100"
                onClick={() => setCartOpen(true)}
              >
                <ShoppingCart className="w-5 h-5 text-slate-600" />
                {cartCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center border-2 border-white">
                    {cartCount}
                  </span>
                )}
              </Button>
            )}

            {user && (
              <div className="hidden sm:flex items-center gap-3">
                {user.picture ? (
                  <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
                ) : (
                  <div className="w-8 h-8 bg-gradient-to-br from-indigo-100 to-indigo-200 rounded-full flex items-center justify-center">
                    <span className="text-indigo-700 font-semibold text-sm">
                      {user.name?.charAt(0) || 'U'}
                    </span>
                  </div>
                )}
                <div className="hidden md:block">
                  <p className="text-sm font-medium text-slate-900">{user.name}</p>
                  <p className="text-xs text-slate-500">{user.email}</p>
                </div>
              </div>
            )}
            <Button
              variant="outline"
              size="sm"
              className="rounded-full border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sign Out
            </Button>
          </div>
        </header>

        {/* Page Content - Scrollable */}
        <div className="flex-1 overflow-y-auto p-4 lg:p-8 custom-scrollbar">
          {/* Every route stays registered so bookmarks resolve; ModuleRoute
              sends the never-enabled case back to the overview, while an
              off-but-once-enabled module renders read-only. */}
          <Routes>
            <Route index element={<ResellerOverview store={store} onUpdate={fetchData} />} />
            <Route path="settings" element={<ResellerSettings store={store} onUpdate={fetchData} />} />
            <Route path="pricing" element={<ModuleRoute module="edisplayroom"><ResellerPricing store={store} onUpdate={fetchData} /></ModuleRoute>} />
            <Route path="products" element={<ModuleRoute module="edisplayroom"><ResellerProducts store={store} /></ModuleRoute>} />
            <Route path="collections" element={<ModuleRoute module="edisplayroom"><ResellerCollections store={store} /></ModuleRoute>} />
            <Route path="orders" element={<ModuleRoute module="edisplayroom"><ResellerOrders store={store} /></ModuleRoute>} />
            <Route path="earnings" element={<ResellerEarnings />} />
            <Route path="funeral-store" element={<ResellerFuneralStore store={store} />} />
            <Route path="funeral-store/product/:id" element={<FuneralStoreProductDetails />} />
            <Route path="funeral-store/checkout" element={<Checkout />} />
            <Route path="my-purchases" element={<ResellerMyOrders />} />
            {/* Infinity Touch — FH print order management */}
            <Route path="print-records" element={<ModuleRoute module="infinity_touch"><SavedPrintOrders /></ModuleRoute>} />
            <Route path="print-records/:recordId" element={<ModuleRoute module="infinity_touch"><PrintRecordDetail /></ModuleRoute>} />
            {/* Placing an order is a write, so it needs the module actually on. */}
            <Route path="print-records/:recordId/order" element={<ModuleRoute module="infinity_touch" requireWritable><PrintOrderFlow /></ModuleRoute>} />
            <Route path="print-orders" element={<ModuleRoute module="infinity_touch"><PrintOrders /></ModuleRoute>} />
            <Route path="commission-ledger" element={<ModuleRoute module="infinity_touch"><CommissionLedger /></ModuleRoute>} />
          </Routes>
        </div>
      </main>

      {!isCheckoutPage && (
        <CartSheet
          onCheckout={() => {
            setCartOpen(false);
            navigate("/reseller/funeral-store/checkout");
          }}
        />
      )}
    </div>
    </ModuleProvider>
  );
}
